import shippingService from '../services/shipping'
import {
  SET_SHIPPING_METHODS,
  SET_CURRENT_SHIPPING_METHOD,
  SET_SHIPPING_LOADING,
  SET_SHIPPING_ERROR,
  UPDATE_SHIPPING_METHOD,
  REMOVE_SHIPPING_METHOD
} from '../mutation-types'

// State
export const state = {
  methods: [],
  currentMethod: null,
  loading: false,
  error: null
}

// Getters
export const getters = {
  shippingMethods: state => state.methods,
  currentShippingMethod: state => state.currentMethod,
  shippingLoading: state => state.loading,
  shippingError: state => state.error,
  defaultMethod: state => state.methods.find(m => m.is_default)
}

// Mutations
export const mutations = {
  [SET_SHIPPING_METHODS](state, methods) {
    state.methods = methods
  },

  [SET_CURRENT_SHIPPING_METHOD](state, method) {
    state.currentMethod = method
  },

  [SET_SHIPPING_LOADING](state, loading) {
    state.loading = loading
  },

  [SET_SHIPPING_ERROR](state, error) {
    state.error = error
  },

  [UPDATE_SHIPPING_METHOD](state, updatedMethod) {
    const index = state.methods.findIndex(m => m.id === updatedMethod.id)
    if (index !== -1) {
      state.methods.splice(index, 1, updatedMethod)
    }
  },

  [REMOVE_SHIPPING_METHOD](state, id) {
    state.methods = state.methods.filter(m => m.id !== id)
  }
}

// Actions
export const actions = {
  async fetchShippingMethods({ commit }, { admin = false } = {}) {
    commit(SET_SHIPPING_LOADING, true)
    commit(SET_SHIPPING_ERROR, null)
    try {
      const { data } = admin
        ? await shippingService.getAllAdmin()
        : await shippingService.getAll()
      commit(SET_SHIPPING_METHODS, data.results || [])
      return data
    } catch (error) {
      commit(SET_SHIPPING_ERROR, error.response?.data?.message || 'Failed to fetch shipping methods')
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async fetchShippingMethod({ commit }, id) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.getById(id)
      commit(SET_CURRENT_SHIPPING_METHOD, data.data)
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async createShippingMethod({ commit, dispatch }, payload) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.create(payload)
      await dispatch('fetchShippingMethods', { admin: true })
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async updateShippingMethod({ commit, dispatch }, { id, payload }) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.update(id, payload)
      await dispatch('fetchShippingMethods', { admin: true })
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async deleteShippingMethod({ commit, dispatch }, id) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.delete(id)
      commit(REMOVE_SHIPPING_METHOD, id)
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async setDefaultMethod({ commit, dispatch }, id) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.setDefault(id)
      await dispatch('fetchShippingMethods', { admin: true })
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async updateServices({ commit, dispatch }, { methodId, services }) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.updateServices(methodId, services)
      await dispatch('fetchShippingMethods', { admin: true })
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async addService({ commit, dispatch }, { methodId, payload }) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.addService(methodId, payload)
      await dispatch('fetchShippingMethods', { admin: true })
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  },

  async deleteService({ commit, dispatch }, { methodId, serviceId }) {
    commit(SET_SHIPPING_LOADING, true)
    try {
      const { data } = await shippingService.deleteService(methodId, serviceId)
      await dispatch('fetchShippingMethods', { admin: true })
      return data
    } catch (error) {
      throw error
    } finally {
      commit(SET_SHIPPING_LOADING, false)
    }
  }
}
